//#regiom imports
import { useEffect, useState } from "react"

// tremor
import { Flex } from "@tremor/react";

// custom component
import { CustomSpinner } from "_components/atoms";

// redyx
import { selectIata } from "_redux/slices/main-screen/user";
import { createEquipment, getStaffEquipments, resetStatus, selectStaffEquipmentsStatus, updateEquipments } from "_redux/slices/timeline/staff-equipments";

// styling
import { Spacing } from "_styles";

// bootstrap
import { Button, Row, Col, CloseButton } from "react-bootstrap";

// redux
import { useDispatch, useSelector } from "react-redux";

// utils
import { normalizeText } from "_utils/functions";

//#endregion

const toItem = (val) => {
    const v = normalizeText(val);
    return v ? { id: v.toLowerCase(), title: v } : null;
};

const AddStaffEquipments = ({ addForm, wheelchairList, vehicleList, setShowModal }) => {

    //#region redux
    const dispatch = useDispatch()

    // redux states
    const iata = useSelector(selectIata)
    const status = useSelector(selectStaffEquipmentsStatus);

    //#endregion

    //#region local states
    const [typingVehicle, setTypingVehicle] = useState("");
    const [typingWheelchair, setTypingWheelchair] = useState("");
    const [vehicles, setVehicles] = useState([]);
    const [wheelchairs, setWheelchairs] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    //#endregion

    //#region effects

    // set initial vehicles and wheelchairs
    useEffect(() => {
        setVehicles(Array.isArray(vehicleList) ? vehicleList : []);
        setWheelchairs(Array.isArray(wheelchairList) ? wheelchairList : []);
    }, [vehicleList, wheelchairList]);

    // handle submission result
    useEffect(() => {
        if (submitting && status === "succeeded") {
            setShowModal(false);
            setSubmitting(false);
            dispatch(getStaffEquipments(iata))
        }
    }, [status, submitting, setShowModal]);

    //#endregion

    //#region functions

    // add vehicle to list
    const addVehicle = (raw) => {

        // convert to item to ensure valid format
        const item = toItem(raw);

        // if invalid, return
        if (!item) return;

        // add to list if not already present
        setVehicles((prev) => {
            if (prev.some((x) => x.id.toLowerCase() === item.id.toLowerCase())) return prev;
            return [...prev, item];
        });
    };

    // add wheelchair to list
    const addWheelchair = (raw) => {

        // convert to item to ensure valid format
        const item = toItem(raw);

        // if invalid, return
        if (!item) return;

        // add to list if not already present
        setWheelchairs((prev) => {
            if (prev.some((x) => x.id.toLowerCase() === item.id.toLowerCase())) return prev;
            return [...prev, item];
        });
    };

    // remove vehicle from list
    const removeVehicle = (id) => setVehicles((prev) => prev.filter((x) => x.id !== id));

    // remove wheelchair from list
    const removeWheelchair = (id) => setWheelchairs((prev) => prev.filter((x) => x.id !== id));

    // handle vehicle add button click
    const handleAddVehicle = () => {
        if (normalizeText(typingVehicle)) {
            addVehicle(typingVehicle);
            setTypingVehicle("");
        }
    };

    // handle wheelchair add button click
    const handleAddWheelchair = () => {
        if (normalizeText(typingWheelchair)) {
            addWheelchair(typingWheelchair);
            setTypingWheelchair("");
        }
    };

    // handle Enter key for vehicle input (optional convenience)
    const handleVehicleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddVehicle();
        }
    };

    // handle Enter key for wheelchair input (optional convenience)
    const handleWheelchairKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddWheelchair();
        }
    };

    // save equipments to backend
    const submitEquipments = () => {
        setSubmitting(true);
        const data = {
            airport: iata,
            vehicleList: vehicles,
            wheelchairList: wheelchairs,
        };
        
        if (addForm) {
            console.log("createEquipment");
            dispatch(createEquipment(data));
        } else {
            const { airport, ...restData } = data
            console.log(restData);
            dispatch(updateEquipments({ id: iata, body: restData }));
        }
    };

    const EquipmentList = ({ items, onRemove }) => (
        <div className="border rounded" style={{ minHeight: 38, maxHeight: 200, overflowY: 'auto' }}>
            {items.length === 0 ? (
                null
            ) : (
                <ul className="list-group list-group-flush mb-0">
                    {items.map((item) => (
                        <li 
                            key={item.id} 
                            className="list-group-item d-flex justify-content-between align-items-center px-3 py-2"
                            style={{ borderBottom: '1px solid #dee2e6' }}
                        >
                            <span>{item.title}</span>
                            <CloseButton onClick={() => onRemove(item.id)} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );

    return (
        <>
            <Row className="g-4">

                {/* vehicle list */}
                <Col md={6}>

                    {/* title */}
                    <h6 className="fw-bold mb-2">Vehicles</h6>

                    {/* vehicle list */}
                    { vehicles.length > 0 && <EquipmentList items={vehicles} onRemove={removeVehicle} /> }

                    {/* input form with add button */}
                    <div className="d-flex gap-2 mt-2">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Type a vehicle name"
                            value={typingVehicle}
                            onChange={(e) => setTypingVehicle(e.target.value)}
                            onKeyDown={handleVehicleKeyDown}
                        />
                        <Button 
                            variant="primary" 
                            onClick={handleAddVehicle}
                            disabled={!normalizeText(typingVehicle)}
                        >
                            Add
                        </Button>
                    </div>

                    {/* description */}
                    <small className="text-muted">Examples: Van, Bus, Car, Ambulift</small>

                </Col>

                {/* wheelchair list */}
                <Col md={6}>

                    {/* title */}
                    <h6 className="fw-bold mb-2">Wheelchairs</h6>

                    {/* wheelchair list */}
                    { wheelchairs.length > 0 && <EquipmentList items={wheelchairs} onRemove={removeWheelchair} /> }

                    {/* input form with add button */}
                    <div className="d-flex gap-2 mt-2">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Type a wheelchair name"
                            value={typingWheelchair}
                            onChange={(e) => setTypingWheelchair(e.target.value)}
                            onKeyDown={handleWheelchairKeyDown}
                        />
                        <Button 
                            variant="primary" 
                            onClick={handleAddWheelchair}
                            disabled={!normalizeText(typingWheelchair)}
                        >
                            Add
                        </Button>
                    </div>
                    <small className="text-muted">Examples: WCHR, WCHS, WCHC, Aisle chair</small>

                </Col>
            </Row>

            {/* buttons */}
            <div style={{ marginTop: Spacing.SCALE_22 }}>
                <Flex>
                    <Button 
                        variant="danger" 
                        onClick={() => setShowModal(false)}>{"Cancel"}</Button>
                    <Button 
                        style={{ width:'200px' }}
                        onClick={submitEquipments} 
                        variant={"primary"} 
                        disabled={wheelchairs.length === 0 && vehicles.length === 0}>{status == "loading" ? (<CustomSpinner size={'small'} variant={'light'} />) : ("Save")}</Button>
                </Flex>
            </div>

        </>
    )
}

export default AddStaffEquipments